import React from 'react'
import ReactDOM from 'react-dom/client'
import { createBrowserRouter, RouterProvider } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { IconoirProvider } from 'iconoir-react'
import * as Sentry from '@sentry/react'
import App from './App'
import RootRouteError from './components/RootRouteError'
import { filterPrivateProfileEvent } from './lib/private-profile-telemetry'
import { ToastProvider } from './components/ui/Toast'
import './styles/globals.css'
import './themes/package-themes.css'
import './themes/package-map.css'

// ============================================================================
// Sentry — Frontend error tracking
// Creates a separate clairvos-admin project in Sentry, or uses workers DSN.
// Set VITE_SENTRY_DSN in CF Pages env vars, or falls back to hardcoded DSN.
// ============================================================================
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN
  || 'https://e7e1bea9170ef43079f8adc79243621f@o4510338616459264.ingest.us.sentry.io/4510865070424064'

Sentry.init({
  dsn: SENTRY_DSN,
  environment: import.meta.env.MODE, // 'production' or 'development'
  // Only track in production — don't spam Sentry during local dev
  enabled: import.meta.env.PROD,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({ blockSelector: '[data-private-profile]' }),
  ],
  beforeSend: event => filterPrivateProfileEvent(event, window.location.pathname),
  beforeBreadcrumb: breadcrumb => filterPrivateProfileEvent(breadcrumb, window.location.pathname),
  // Performance: sample 10% of transactions
  tracesSampleRate: 0.1,
  // Session Replay: record 0% of sessions normally, 100% of sessions with errors
  replaysSessionSampleRate: 0.0,
  replaysOnErrorSampleRate: 1.0,
  // Tag all events so we can distinguish from worker errors
  initialScope: {
    tags: { app: 'admin-console' },
  },
})

// React Query client with sensible defaults
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30 * 1000, // 30 seconds
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
})

// Keep existing descendant routes while enabling reliable navigation blocking.
const router=createBrowserRouter([{path:'*',element:<><App/><ToastProvider/></>,errorElement:<RootRouteError/>}])

ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <Sentry.ErrorBoundary
      fallback={({ error, resetError }) => (
        <div style={{ padding: '2rem', textAlign: 'center', fontFamily: 'system-ui' }}>
          <h2 style={{ marginBottom: '1rem' }}>Something went wrong</h2>
          <p style={{ color: '#666', marginBottom: '1rem' }}>{error?.message || 'An unexpected error occurred'}</p>
          <button
            onClick={resetError}
            style={{
              padding: '0.5rem 1.5rem',
              borderRadius: '0.5rem',
              border: '1px solid #ddd',
              cursor: 'pointer',
              marginRight: '0.5rem',
            }}
          >
            Try Again
          </button>
          <button
            onClick={() => window.location.href = '/'}
            style={{
              padding: '0.5rem 1.5rem',
              borderRadius: '0.5rem',
              border: 'none',
              background: '#864AEC',
              color: 'white',
              cursor: 'pointer',
            }}
          >
            Go to Dashboard
          </button>
        </div>
      )}
    >
      <QueryClientProvider client={queryClient}>
        {/* Thin weight, globally — every Iconoir icon renders at strokeWidth 1.5 */}
        <IconoirProvider iconProps={{ strokeWidth: 1.5 }}>
          <RouterProvider router={router} />
        </IconoirProvider>
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  </React.StrictMode>,
)
